import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { SeniorModeProvider } from "@/contexts/SeniorModeContext";
import { OfflineProvider } from "@/contexts/OfflineContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { UiOverridesProvider } from "@/contexts/UiOverridesContext";
import AppRoutes from "@/AppRoutes";
import MaintenancePage from "@/pages/MaintenancePage";
import { Toaster } from "@/components/ui/sonner";
import { CopyrightStrip } from "@/components/CopyrightStrip";
import { PopMessageProvider } from "@/lib/popMessage";
import { FloatingHelpButton } from "@/components/FloatingHelpButton";
import DashboardAssistant from "@/components/DashboardAssistant";
import { PwaInstallNudge } from "@/components/PwaInstallNudge";
import SuccessCelebration from "@/components/SuccessCelebration";
import ContentProtection from "@/components/ContentProtection";
import { api } from "@/lib/api";
import { setPopupConfig } from "@/lib/feedback";
import { captureAndTrack } from "@/lib/attribution";

/**
 * MaintenanceGate — polls /api/public/maintenance-status every 30s. When
 * maintenance is ON and the user is NOT on an /admin/* route, we render the
 * full-screen maintenance page instead of AppRoutes. Super admin can still
 * navigate to /admin/login → /admin/* to disable maintenance.
 */
function MaintenanceGate({ children }) {
    const [state, setState] = useState({ enabled: false, message: "" });
    const location = useLocation();
    useEffect(() => {
        let alive = true;
        const load = () => {
            api.get("/public/maintenance-status")
                .then(({ data }) => alive && setState({ enabled: !!data.enabled, message: data.message || "" }))
                .catch(() => {});
        };
        load();
        const t = setInterval(load, 30000);
        return () => { alive = false; clearInterval(t); };
    }, []);
    const isAdminRoute = location.pathname.startsWith("/admin");
    if (state.enabled && !isAdminRoute) {
        return <MaintenancePage message={state.message} />;
    }
    return children;
}

function App() {
    useEffect(() => {
        const s = localStorage.getItem("vt_text_scale");
        if (s === "sm") document.documentElement.style.fontSize = "15px";
        else if (s === "lg") document.documentElement.style.fontSize = "18px";
    }, []);

    useEffect(() => {
        api.get("/popup-config").then(({ data }) => setPopupConfig(data)).catch(() => {});
    }, []);

    useEffect(() => {
        captureAndTrack();
    }, []);

    const skipToContent = (e) => {
        e.preventDefault();
        const m = document.querySelector("main") || document.getElementById("root");
        if (m) {
            m.setAttribute("tabindex", "-1");
            m.focus({ preventScroll: false });
            m.scrollIntoView();
        }
    };
    return (
        <div className="App">
            <a href="#main" className="skip-link" data-testid="skip-to-content" onClick={skipToContent}>
                Skip to main content
            </a>
            <OfflineProvider>
                <BrandingProvider>
                  <UiOverridesProvider>
                    <LanguageProvider>
                        <BrowserRouter>
                            <AuthProvider>
                                <SeniorModeProvider>
                                    <MaintenanceGate>
                                        <AppRoutes />
                                        <FloatingHelpButton />
                                        <DashboardAssistant />
                                        <PwaInstallNudge />
                                        <CopyrightStrip />
                                        <PopMessageProvider />
                                    </MaintenanceGate>
                                </SeniorModeProvider>
                            </AuthProvider>
                        </BrowserRouter>
                    </LanguageProvider>
                  </UiOverridesProvider>
                </BrandingProvider>
            </OfflineProvider>
            <Toaster richColors position="top-center" />
            <SuccessCelebration />
            <ContentProtection />
        </div>
    );
}

export default App;
